import { useMemo } from 'react';
import { Link, useNavigate } from 'react-router';
import { Button, Dropdown, Flex, MenuOutlined, type MenuProps } from '@/uicomponents';
import { Show } from '@/components/shared';
import { ROUTES } from '@/lib/constants/routes';
import { HEADER_BUTTON_STYLES } from './header-button-styles';
import styles from './app-header.module.css';

export interface AppHeaderNavProps {
  /** Below `md` the tabs fold into one menu button, so the header fits at 375px. */
  compact: boolean;
  /** The Home item shows only while the `3rd-eye-home` flag is on (NPJB-899). */
  homeAllowed: boolean;
  isHomeActive: boolean;
  intelligenceAllowed: boolean;
  intelligenceTo: string;
  isIntelligenceActive: boolean;
  isActivateActive: boolean;
}

const tabClass = (active: boolean) => (active ? `${styles.tab} ${styles.tabActive}` : styles.tab);

/** The header's product tabs: inline links on desktop, a menu on small screens. */
export const AppHeaderNav = ({
  compact,
  homeAllowed,
  isHomeActive,
  intelligenceAllowed,
  intelligenceTo,
  isIntelligenceActive,
  isActivateActive,
}: AppHeaderNavProps) => {
  const navigate = useNavigate();

  const menu: MenuProps = useMemo(() => {
    const items: NonNullable<MenuProps['items']> = [
      { key: ROUTES.campaigns, label: 'Activate', onClick: () => navigate(ROUTES.campaigns) },
    ];
    if (intelligenceAllowed) {
      items.unshift({
        key: intelligenceTo,
        label: 'Signal Intelligence',
        onClick: () => navigate(intelligenceTo),
      });
    }
    if (homeAllowed) {
      items.unshift({ key: ROUTES.home, label: 'Home', onClick: () => navigate(ROUTES.home) });
    }
    const selected = [
      ...(isHomeActive ? [ROUTES.home] : []),
      ...(isIntelligenceActive ? [intelligenceTo] : []),
      ...(isActivateActive ? [ROUTES.campaigns] : []),
    ];
    return { items, selectable: true, selectedKeys: selected };
  }, [
    navigate,
    homeAllowed,
    isHomeActive,
    intelligenceAllowed,
    intelligenceTo,
    isIntelligenceActive,
    isActivateActive,
  ]);

  if (compact) {
    return (
      <Dropdown menu={menu} trigger={['click']} placement='bottomRight'>
        <Button
          type='text'
          shape='circle'
          size='middle'
          aria-label='Main menu'
          icon={<MenuOutlined />}
          style={HEADER_BUTTON_STYLES.navMenu}
        />
      </Dropdown>
    );
  }

  return (
    <Flex align='center' gap={24} component='nav' aria-label='Main'>
      <Show when={homeAllowed}>
        <Link to={ROUTES.home} className={tabClass(isHomeActive)}>
          Home
        </Link>
      </Show>
      <Show when={intelligenceAllowed}>
        {/* NPJB-586: the audience page — the one last open, else the default audience. */}
        <Link to={intelligenceTo} className={tabClass(isIntelligenceActive)}>
          Signal Intelligence
        </Link>
      </Show>
      <Link to={ROUTES.campaigns} className={tabClass(isActivateActive)}>
        Activate
      </Link>
    </Flex>
  );
};
